import { queryOptions, useQuery } from "@tanstack/react-query";
import * as z from "zod";
import { api } from "../http";
import { userSchema } from "./users";
import { policySchema } from "@/lib/queries/policies";
import { paginatedListSchema } from "@/lib/utils/zod";

const groupBaseSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().min(1, "Description is required"),
});
export const groupFormSchema = groupBaseSchema.extend({
  users: z.array(z.uuid()).optional(),
  policies: z.array(z.number().int()).optional(),
});
export const groupSchema = groupBaseSchema.extend({
  id: z.uuid(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  created_by_id: z.uuid(),
});
export const groupsListSchema = paginatedListSchema(groupSchema);

export const groupUserAssociationSchema = z.object({
  user: userSchema,
  added_at: z.string(),
});

export const groupUsersListSchema = paginatedListSchema(
  groupUserAssociationSchema,
);

export const groupPolicyAssociationSchema = policySchema.extend({
  added_at: z.string(),
});

export type GroupFormData = z.infer<typeof groupFormSchema>;
export type Group = z.infer<typeof groupSchema>;
export type GroupsList = z.infer<typeof groupsListSchema>;
export type GroupUserAssociation = z.infer<typeof groupUserAssociationSchema>;
export type GroupUsersList = z.infer<typeof groupUsersListSchema>;
export type GroupPolicyAssociation = z.infer<
  typeof groupPolicyAssociationSchema
>;

type PaginationParams = {
  page: number;
  per_page?: number;
  search?: string;
};

export function groupsListQueryOptions(params: PaginationParams) {
  return queryOptions({
    queryKey: ["groups", "list", params],
    queryFn({ signal }) {
      const searchParams = new URLSearchParams({
        page: params.page.toString(),
        per_page: (params.per_page || 10).toString(),
      });
      if (params.search) searchParams.set("search", params.search);

      return api.raw
        .get("api/v1/groups/", { searchParams, signal })
        .json<GroupsList>();
    },
  });
}

export function groupQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["groups", "detail", id],
    queryFn() {
      return api.raw.get(`api/v1/groups/${id}`).json<Group>();
    },
  });
}

type GroupUsersSearchParams = {
  page: number;
  per_page?: number;
};

export function groupUsersListQueryOptions(
  groupId: string,
  params: GroupUsersSearchParams,
) {
  return queryOptions({
    queryKey: ["groups", groupId, "users", params],
    queryFn() {
      return api.raw
        .get(`api/v1/groups/${groupId}/users`, {
          searchParams: {
            page: params.page.toString(),
            per_page: (params.per_page ?? 10).toString(),
          },
        })
        .json<GroupUsersList>();
    },
  });
}
export function groupPoliciesQueryOptions(groupId: string) {
  return queryOptions({
    queryKey: ["groups", groupId, "policies"],
    queryFn() {
      return api.raw
        .get(`api/v1/groups/${groupId}/policies`)
        .json<Array<GroupPolicyAssociation>>();
    },
  });
}

// Hooks
export function useGroupsList(params: PaginationParams) {
  return useQuery(groupsListQueryOptions(params));
}

export function useGroup(id: string) {
  return useQuery(groupQueryOptions(id));
}

export function useGroupUsersList(
  groupId: string,
  params: GroupUsersSearchParams,
) {
  return useQuery(groupUsersListQueryOptions(groupId, params));
}

export function useGroupPolicies(groupId: string) {
  return useQuery(groupPoliciesQueryOptions(groupId));
}
